<script setup lang="ts">
import { computed } from 'vue'
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon } from '@heroicons/vue/24/outline'

interface Props {
  label: string
  value: number | null
  delta?: number | null
  deltaSign?: 'up' | 'down' | 'flat' | null
  hint?: string
  loading?: boolean
  /** Pour les KPI instantanés où le delta n'a pas de sens (ex: "with access") */
  hideDelta?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  delta: null,
  deltaSign: null,
  hint: '',
  loading: false,
  hideDelta: false,
})

const deltaIcon = computed(() => {
  if (props.deltaSign === 'up') return ArrowTrendingUpIcon
  if (props.deltaSign === 'down') return ArrowTrendingDownIcon
  return MinusIcon
})

const deltaClass = computed(() => {
  if (props.deltaSign === 'up') return 'text-success'
  if (props.deltaSign === 'down') return 'text-error'
  return 'text-base-content/40'
})

const formattedDelta = computed(() => {
  if (props.delta === null || props.delta === undefined) return ''
  const sign = props.delta > 0 ? '+' : ''
  return `${sign}${props.delta}`
})

const formattedValue = computed(() => {
  if (props.value === null || props.value === undefined) return '—'
  return props.value.toLocaleString('fr-FR')
})
</script>

<template>
  <div class="card bg-base-100 shadow border border-base-300">
    <div class="card-body p-5">
      <div class="text-sm text-base-content/70 font-medium">{{ label }}</div>

      <div class="mt-2 text-3xl font-bold tabular-nums">
        <span v-if="loading" class="text-base-content/40">…</span>
        <span v-else>{{ formattedValue }}</span>
      </div>

      <div class="mt-2 flex items-center gap-1.5 text-sm" v-if="!hideDelta">
        <template v-if="loading">
          <span class="text-base-content/40">—</span>
        </template>
        <template v-else>
          <component :is="deltaIcon" :class="deltaClass" class="w-4 h-4" aria-hidden="true" />
          <span :class="deltaClass" class="tabular-nums font-medium">{{ formattedDelta }}</span>
          <span class="text-base-content/50">vs période précédente</span>
        </template>
      </div>

      <div v-if="hint && !hideDelta" class="text-xs text-base-content/50 mt-1">{{ hint }}</div>
      <div v-else-if="hideDelta" class="text-xs text-base-content/50 mt-1">instantané</div>
    </div>
  </div>
</template>
